import React, { ReactNode, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Input, Form as AntForm, Typography, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import ReadonlyField from 'technical/form/formik/ReadonlyField';
import Flex from 'ui/flex';
import {
  StatusEnum,
  isStatusInProgress,
  isStatusPending,
} from 'business/evaluationRequest/utils/evaluationRequestStatus';
import {
  AuthorizedAccessOnly,
  technicianOnly,
} from 'business/user/services/policy';
import { format } from 'date-fns';
import { useTechnologiesList } from 'business/technology/hooks/queries';
import { useProductsList } from 'business/product/hooks/queries';
import {
  SearchableSelectArtsReference,
  SearchableSelectProductionDesignation,
} from 'business/productDesignation/component/select';
import { SearchableSelectClientSupplier } from 'business/clientSupplier/component/select';
import DatePickerField from 'technical/form/formik/datePickerField';
import classNames from 'classnames';
import { SimpleFormValues } from '../../types';
import InputError from '../../../../ui/form/inputError';
import Spacer from '../../../../ui/spacer';
import styles from './index.module.scss';
import { usePrioritiesList } from '../../../priority/hooks/queries';
import { useCategoriesList } from '../../../category/hooks/queries';
import { computeDateFromPriority } from '../../../priority/utils/computeDateFromPriority';
import InputField from '../../../../technical/form/formik/InputField';
import SelectField from '../../../../technical/form/formik/selectField';
import EvaluationStepsFieldArray from '../evaluationStepsCreationForm/EvaluationStepsFieldArray';
import CommentsAndConclusion from '../commentsAndConclusion';

const { TextArea } = Input;

interface SimpleCreateEvaluationRequestFormProps {
  error: { key: string; context: string };
  userName: string | null;
  actions: ReactNode;
  editableFields: string[];
}

export default function SimpleCreateEvaluationRequestForm({
  actions,
  error,
  userName,
  editableFields,
}: SimpleCreateEvaluationRequestFormProps) {
  const { t } = useTranslation();
  const {
    // values includes advanced and simple form values cf creationFormsTab
    values,
    setFieldValue,
  } = useFormikContext<SimpleFormValues>(); // we use a partial type from the upper form
  const { data: priorities } = usePrioritiesList({});
  const { data: categories } = useCategoriesList({ formType: 'simple' });
  const { data: technologies } = useTechnologiesList({});
  const { data: products } = useProductsList({});
  const selectedPriority = priorities?.find(
    (priority) => priority.id === values.priorityId,
  );

  // requestDate and maxStartDate are only used for display purposes, they are readonly and are not send to the back
  const requestDate = values.requestDate || new Date();

  // maxStartDate | factoryDepartureDateOfEntry
  // is computed locally but could be defined by back on update page
  // maybe it would be a betters solution to set maxStartDate in form values on priority on change
  const maxStartDate = selectedPriority
    ? computeDateFromPriority(selectedPriority, requestDate)
    : null;

  // After switching between simple and advanced creation form
  // we must check if the selected category is still allowed
  // if not, we remove the selected category
  useEffect(() => {
    if (!categories || categories.length === 0 || !values.categoryId) {
      return;
    }
    if (!categories.map((c) => c.id).includes(values.categoryId)) {
      setFieldValue('categoryId', null);
    }
  }, [categories]);

  return (
    <>
      <Typography.Title level={4}>
        {t('evaluationRequest.creation.simple.title')}
      </Typography.Title>
      <AntForm className={styles.form} layout="vertical">
        <Row gutter={32}>
          <Col span={10}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.subject')}
            >
              <InputField<SimpleFormValues>
                valueKey="subject"
                component={TextArea}
                showCount
                maxLength={500}
                readOnly={!editableFields.includes('subject')}
              />
            </AntForm.Item>
          </Col>
          <Col span={4}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.applicant')}
            >
              <ReadonlyField value={userName ?? ''} />
            </AntForm.Item>
          </Col>
          {values.statusId !== StatusEnum.DRAFT &&
          values.statusId !== StatusEnum.WAITING_VALIDATION ? (
            <Col span={4}>
              <AntForm.Item
                label={t(
                  'evaluationRequest.creation.simple.technicianInCharge',
                )}
              >
                <ReadonlyField
                  value={
                    values.technician
                      ? `${values.technician.firstname} ${values.technician.lastname}`
                      : ''
                  }
                />
              </AntForm.Item>
            </Col>
          ) : null}
        </Row>
        <Row gutter={32}>
          <Col span={4}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.requestDate')}
            >
              <ReadonlyField value={format(requestDate, 'dd/MM/yyyy')} />
            </AntForm.Item>
          </Col>
          <Col span={4}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.priority')}
            >
              <SelectField<SimpleFormValues>
                valueKey="priorityId"
                optionsList={priorities}
                readOnly={!editableFields.includes('priorityId')}
              />
            </AntForm.Item>
          </Col>
          <Col span={4}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.maxStartDate')}
            >
              <ReadonlyField
                value={
                  maxStartDate
                    ? format(maxStartDate, 'dd/MM/yyyy')
                    : t('evaluationRequest.creation.no-priority-selected')
                }
              />
            </AntForm.Item>
          </Col>
          <Col span={4}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.estimatedEndDate')}
            >
              <DatePickerField<SimpleFormValues>
                valueKey="estimatedEndDate"
                readOnly={!editableFields.includes('estimatedEndDate')}
              />
            </AntForm.Item>
          </Col>
          <Col span={4}>
            <AntForm.Item
              label={t(
                'evaluationRequest.creation.simple.intermediateExitDate',
              )}
            >
              <DatePickerField<SimpleFormValues>
                valueKey="intermediateExitDate"
                readOnly={!editableFields.includes('intermediateExitDate')}
              />
            </AntForm.Item>
          </Col>
        </Row>
        <Row gutter={32}>
          <Col span={6}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.technology')}
            >
              <SelectField<SimpleFormValues>
                valueKey="technologyId"
                optionsList={technologies}
                readOnly={!editableFields.includes('technologyId')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.product')}
            >
              <SelectField<SimpleFormValues>
                valueKey="productId"
                optionsList={products}
                readOnly={!editableFields.includes('productId')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.category')}
            >
              <SelectField<SimpleFormValues>
                valueKey="categoryId"
                optionsList={categories}
                readOnly={!editableFields.includes('categoryId')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.clientSupplier')}
            >
              <SearchableSelectClientSupplier<SimpleFormValues>
                valueKey="clientSupplierId"
                readOnly={!editableFields.includes('clientSupplierId')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.projectNumber')}
            >
              <InputField<SimpleFormValues>
                valueKey="project"
                readOnly={!editableFields.includes('project')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.lotNumber')}
            >
              <InputField<SimpleFormValues>
                valueKey="lotNumber"
                readOnly={!editableFields.includes('lotNumber')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.commandNumber')}
            >
              <InputField<SimpleFormValues>
                valueKey="commandNumber"
                readOnly={!editableFields.includes('commandNumber')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.orderedQuantity')}
            >
              <InputField<SimpleFormValues>
                valueKey="orderedQuantity"
                type="number"
                min={0}
                readOnly={!editableFields.includes('orderedQuantity')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.BRNumber')}
            >
              <InputField<SimpleFormValues>
                valueKey="BRNumber"
                readOnly={!editableFields.includes('BRNumber')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.purchaseOrder')}
            >
              <InputField<SimpleFormValues>
                valueKey="purchaseOrder"
                readOnly={!editableFields.includes('purchaseOrder')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.qualityPlanNumber')}
            >
              <InputField<SimpleFormValues>
                valueKey="qualityPlanNumber"
                readOnly={!editableFields.includes('qualityPlanNumber')}
              />
            </AntForm.Item>
          </Col>
        </Row>
        <Row gutter={32}>
          <Col span={6}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.productDesignation')}
            >
              <SearchableSelectProductionDesignation<SimpleFormValues>
                valueKey="productDesignationId"
                readOnly={!editableFields.includes('productDesignationId')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              required
              label={t('evaluationRequest.creation.simple.referenceArts')}
            >
              <SearchableSelectArtsReference<SimpleFormValues>
                valueKey="productDesignationId"
                readOnly={!editableFields.includes('productDesignationId')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.nominalCapacity')}
              required
            >
              <InputField<SimpleFormValues>
                valueKey="nominalCapacity"
                type="number"
                min={0}
                readOnly={!editableFields.includes('nominalCapacity')}
              />
            </AntForm.Item>
          </Col>
          <Col span={6}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.toTestQuantity')}
              required
            >
              <InputField<SimpleFormValues>
                valueKey="toTestQuantity"
                type="number"
                min={0}
                readOnly={!editableFields.includes('toTestQuantity')}
              />
            </AntForm.Item>
          </Col>
          <Col span={10}>
            <AntForm.Item
              label={t('evaluationRequest.creation.simple.description')}
            >
              <InputField<SimpleFormValues>
                valueKey="description"
                component={TextArea}
                readOnly={!editableFields.includes('description')}
                showCount
                maxLength={500}
              />
            </AntForm.Item>
          </Col>
        </Row>

        {values.statusId === StatusEnum.DONE ? (
          <Spacer direction="vertical">
            <Flex column>
              <CommentsAndConclusion
                readOnlyTechnician={
                  !editableFields.includes('conclusionTechnician')
                }
                readOnlyManager={!editableFields.includes('conclusionManager')}
                readOnlyApplicant={
                  !editableFields.includes('conclusionApplicant')
                }
              />
            </Flex>
          </Spacer>
        ) : null}

        <AuthorizedAccessOnly policy={technicianOnly}>
          {values.statusId &&
            (isStatusInProgress(values.statusId) ||
              isStatusPending(values.statusId)) && (
              <Spacer direction="vertical">
                <Flex column>
                  <EvaluationStepsFieldArray
                    readOnly={!isStatusPending(values.statusId)}
                  />
                </Flex>
              </Spacer>
            )}
        </AuthorizedAccessOnly>

        <InputError
          error={
            error.context ? t(error.key, { context: error.context }) : undefined
          }
        />

        <Spacer />

        <div className={classNames(styles.actionsContainer, styles.noPrint)}>
          {actions}
        </div>
      </AntForm>
    </>
  );
}
